import React, { useEffect, useState, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import BreadcrumbFromOrigin from '../components/BreadcrumbFromOrigin';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, ShoppingCart, Truck, CheckCircle2, Trash2, Package2, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrency, formatCurrency, CompanyContext } from '../contexts/CompanyContext';
import RowActions from '../components/RowActions';
import { downloadEntityPdf } from '../lib/entityPdf';

const MANAGE = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT', 'PROCUREMENT_OFFICER'];
const RECEIVE = [...MANAGE, 'TECHNICIAN', 'CS_SUPERVISOR'];

const STATUS_BADGE = {
  draft:     'bg-slate-100 text-slate-700 border border-slate-200',
  sent:      'bg-sky-100 text-sky-800 border border-sky-200',
  partial:   'bg-amber-100 text-amber-800 border border-amber-200',
  delivered: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
  closed:    'bg-slate-200 text-slate-800 border border-slate-300',
  cancelled: 'bg-rose-100 text-rose-800 border border-rose-200',
};

const STATUS_OPTIONS = ['draft', 'sent', 'partial', 'delivered', 'closed', 'cancelled'];

export default function PurchaseOrders() {
  const { user } = useContext(AuthContext);
  const { symbol } = useCurrency();
  const { company } = useContext(CompanyContext);
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [openPo, setOpenPo] = useState(null);    // for receive dialog
  const [matchOpen, setMatchOpen] = useState(null);

  const [draft, setDraft] = useState({
    po_number: '', supplier_id: '', supplier_name: '', building_id: '',
    issue_date: new Date().toISOString().split('T')[0], expected_delivery: '',
    notes: '', tax_amount: 0,
    line_items: [{ item_name: '', quantity: 1, unit: 'pcs', unit_price: 0, description: '' }],
  });

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [p, s, b, bi] = await Promise.all([
        axios.get(`${API}/purchase-orders`, { withCredentials: true }),
        axios.get(`${API}/suppliers`, { withCredentials: true }),
        axios.get(`${API}/buildings`, { withCredentials: true }),
        MANAGE.includes(user?.role) ? axios.get(`${API}/bills`, { withCredentials: true }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      setPos(p.data || []);
      setSuppliers(s.data || []);
      setBuildings(b.data || []);
      setBills(bi.data || []);
    } catch { toast.error('Failed to load purchase orders'); }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  // Deep-link support: /purchase-orders?highlight=PO-2026-0001 → scroll to
  // and briefly outline the matching PO row (arrives from Purchase Requests).
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const target = searchParams.get('highlight');
    if (!target || loading) return;
    const po = pos.find(p => p.po_number === target);
    if (!po) return;
    const el = document.querySelector(`[data-testid="po-row-${po.id}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('ring-2', 'ring-amber-400', 'ring-offset-2', 'transition');
    const t = setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400', 'ring-offset-2'), 3200);
    return () => clearTimeout(t);
  }, [searchParams, pos, loading]);

  const setLine = (idx, key, value) => {
    setDraft(d => {
      const items = [...d.line_items];
      items[idx] = { ...items[idx], [key]: value };
      return { ...d, line_items: items };
    });
  };

  const addLine = () => setDraft(d => ({ ...d, line_items: [...d.line_items, { item_name: '', quantity: 1, unit: 'pcs', unit_price: 0, description: '' }] }));
  const rmLine = (idx) => setDraft(d => ({ ...d, line_items: d.line_items.filter((_, i) => i !== idx) }));

  const draftSubtotal = (draft.line_items || []).reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0), 0);
  const draftTotal = draftSubtotal + Number(draft.tax_amount || 0);

  const handleCreate = async () => {
    if (!draft.po_number || !draft.supplier_id) {
      toast.error('PO number and supplier are required');
      return;
    }
    if (!draft.line_items.length || draft.line_items.some(l => !l.item_name)) {
      toast.error('Add at least one line item with a name');
      return;
    }
    try {
      const supp = suppliers.find(s => s.id === draft.supplier_id);
      await axios.post(`${API}/purchase-orders`, {
        ...draft,
        supplier_name: supp?.name || draft.supplier_name,
        line_items: draft.line_items.map(l => ({ ...l, quantity: Number(l.quantity), unit_price: Number(l.unit_price) })),
        tax_amount: Number(draft.tax_amount || 0),
      }, { withCredentials: true });
      toast.success('Purchase order created');
      setShowNew(false);
      setDraft({
        po_number: '', supplier_id: '', supplier_name: '', building_id: '',
        issue_date: new Date().toISOString().split('T')[0], expected_delivery: '',
        notes: '', tax_amount: 0,
        line_items: [{ item_name: '', quantity: 1, unit: 'pcs', unit_price: 0, description: '' }],
      });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const cyclePO = async (po, newStatus) => {
    if (!window.confirm(`Set PO ${po.po_number} status to ${newStatus}?`)) return;
    try {
      await axios.put(`${API}/purchase-orders/${po.id}`, { status: newStatus }, { withCredentials: true });
      fetchAll();
    } catch (e) { toast.error('Failed'); }
  };

  const closePO = async (po) => {
    try {
      await axios.post(`${API}/purchase-orders/${po.id}/close`, {}, { withCredentials: true });
      toast.success('PO closed'); fetchAll();
    } catch { toast.error('Close failed'); }
  };

  const deletePO = async (po) => {
    try {
      await axios.delete(`${API}/purchase-orders/${po.id}`, { withCredentials: true });
      toast.success('Deleted'); fetchAll();
    } catch { toast.error('Delete failed'); }
  };

  const downloadPoPdf = (po) => {
    const supplier = suppliers.find(s => s.id === po.supplier_id);
    const b = buildings.find(x => x.id === po.building_id);
    const items = (po.line_items || []).map((li) => [
      li.item_name, String(li.quantity), li.unit || 'pcs',
      formatCurrency(li.unit_price, symbol),
      formatCurrency((li.quantity || 0) * (li.unit_price || 0), symbol),
    ]);
    downloadEntityPdf({
      title: `LPO · ${po.po_number}`,
      subtitle: `${supplier?.name || po.supplier_name || ''} · ${po.status}`,
      company,
      fields: [
        { label: 'LPO #', value: po.po_number },
        ...(po.complaint_ticket_number ? [{ label: 'Complaint Ticket', value: po.complaint_ticket_number }] : []),
        { label: 'Supplier', value: supplier?.name || po.supplier_name || '—' },
        { label: 'Building', value: b?.name || '—' },
        { label: 'Issue date', value: po.issue_date ? new Date(po.issue_date).toLocaleDateString() : '—' },
        { label: 'Expected delivery', value: po.expected_delivery ? new Date(po.expected_delivery).toLocaleDateString() : '—' },
        { label: 'Status', value: po.status },
        { label: 'Subtotal', value: formatCurrency(po.subtotal || 0, symbol) },
        { label: 'Tax', value: formatCurrency(po.tax_amount || 0, symbol) },
        { label: 'Total', value: formatCurrency(po.total_amount || 0, symbol) },
        { label: 'Line items', value: items.length },
      ],
      notes: po.notes,
    });
  };

  return (
    <Layout>
      <div data-testid="purchase-orders-page" className="space-y-6">
        <BreadcrumbFromOrigin sourceLabel="Purchase Request" sourceHref="/purchase-requests" />
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
              <ShoppingCart className="w-6 h-6 text-amber-600" />LPO Preparation
            </h1>
            <p className="text-sm text-muted-foreground">Issue Local Purchase Orders, track deliveries, and match supplier invoices for clean A/P.</p>
          </div>
          {MANAGE.includes(user?.role) && (
            <Dialog open={showNew} onOpenChange={setShowNew}>
              <DialogTrigger asChild>
                <Button data-testid="new-po-btn" className="bg-amber-500 hover:bg-amber-600 text-slate-900"><Plus className="w-4 h-4 mr-1" />New LPO</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle>New Local Purchase Order (LPO)</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2 max-h-[70vh] overflow-y-auto pr-1">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>LPO Number *</Label><Input data-testid="po-number-input" value={draft.po_number} onChange={(e) => setDraft({ ...draft, po_number: e.target.value })} placeholder="LPO-2026-001" /></div>
                    <div><Label>Issue Date</Label><Input type="date" value={draft.issue_date} onChange={(e) => setDraft({ ...draft, issue_date: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Supplier *</Label>
                      <Select value={draft.supplier_id} onValueChange={(v) => setDraft({ ...draft, supplier_id: v })}>
                        <SelectTrigger data-testid="po-supplier-select"><SelectValue placeholder="Pick supplier" /></SelectTrigger>
                        <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Building</Label>
                      <Select value={draft.building_id} onValueChange={(v) => setDraft({ ...draft, building_id: v })}>
                        <SelectTrigger><SelectValue placeholder="(none)" /></SelectTrigger>
                        <SelectContent>{buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div><Label>Expected Delivery</Label><Input type="date" value={draft.expected_delivery} onChange={(e) => setDraft({ ...draft, expected_delivery: e.target.value })} /></div>

                  <div className="border-t pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <Label>Line Items</Label>
                      <Button data-testid="add-line-btn" type="button" size="sm" variant="outline" onClick={addLine}><Plus className="w-3.5 h-3.5 mr-1" />Add line</Button>
                    </div>
                    <div className="space-y-2">
                      {draft.line_items.map((l, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-end p-2 rounded-lg border border-slate-200">
                          <div className="col-span-5"><Label className="text-xs">Item *</Label><Input data-testid={`po-line-name-${i}`} value={l.item_name} onChange={(e) => setLine(i, 'item_name', e.target.value)} /></div>
                          <div className="col-span-2"><Label className="text-xs">Qty</Label><Input type="number" value={l.quantity} onChange={(e) => setLine(i, 'quantity', e.target.value)} /></div>
                          <div className="col-span-1"><Label className="text-xs">Unit</Label><Input value={l.unit} onChange={(e) => setLine(i, 'unit', e.target.value)} /></div>
                          <div className="col-span-3"><Label className="text-xs">Unit Price</Label><Input type="number" value={l.unit_price} onChange={(e) => setLine(i, 'unit_price', e.target.value)} /></div>
                          <div className="col-span-1 text-right">
                            <Button type="button" size="sm" variant="ghost" className="text-red-500" onClick={() => rmLine(i)}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Tax Amount</Label><Input type="number" value={draft.tax_amount} onChange={(e) => setDraft({ ...draft, tax_amount: e.target.value })} /></div>
                    <div className="text-right">
                      <Label>Subtotal</Label>
                      <div className="font-mono text-sm pt-2 text-slate-700">{formatCurrency(draftSubtotal, symbol)}</div>
                      <div className="font-mono text-base font-bold text-foreground">Total: {formatCurrency(draftTotal, symbol)}</div>
                    </div>
                  </div>

                  <div><Label>Notes</Label><Textarea rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} /></div>
                  <Button data-testid="submit-po-btn" onClick={handleCreate} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900">Create PO</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? <div className="py-12 text-center text-muted-foreground">Loading…</div> : (
          <Card className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3">LPO #</th>
                  <th className="px-4 py-3">Supplier</th>
                  <th className="px-4 py-3">Building</th>
                  <th className="px-4 py-3">Issued</th>
                  <th className="px-4 py-3">Items</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pos.map(po => {
                  const items = po.line_items || [];
                  const totQty = items.reduce((s, i) => s + Number(i.quantity || 0), 0);
                  const recv = items.reduce((s, i) => s + Number(i.qty_received || 0), 0);
                  const pending = totQty - recv;
                  const isGrnOpen = po.status === 'partial' || (po.status === 'sent' && recv > 0);
                  const bld = buildings.find(b => b.id === po.building_id);
                  return (
                    <tr key={po.id} data-testid={`po-row-${po.id}`} className={`border-b last:border-0 hover:bg-slate-50 ${isGrnOpen ? 'bg-rose-50/40' : ''}`}>
                      <td className="px-4 py-3 font-mono text-xs font-bold text-foreground">
                        {po.po_number}
                        {po.complaint_ticket_number && (
                          <div data-testid={`po-${po.id}-ticket`} className="mt-1 inline-block px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 border border-amber-500/40 text-[10px] font-semibold">
                            {po.complaint_ticket_number}
                          </div>
                        )}
                        {po.source_pr_number && (
                          <a
                            href={`/purchase-requests?highlight=${encodeURIComponent(po.source_pr_number)}`}
                            data-testid={`po-${po.id}-pr`}
                            className="mt-1 inline-block px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-700 border border-purple-500/40 text-[10px] font-semibold hover:bg-purple-500/20"
                            title="Open the linked Purchase Request"
                          >
                            ← {po.source_pr_number}
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{po.supplier_name}</td>
                      <td className="px-4 py-3 text-slate-700">{bld?.name || '—'}</td>
                      <td className="px-4 py-3 text-slate-700">{po.issue_date}</td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {items.length} lines · received {recv}/{totQty}
                        {isGrnOpen && (
                          <div data-testid={`po-${po.id}-grn-open`} className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-600 text-white text-[10px] font-bold uppercase tracking-wide">
                            GRN OPEN · {pending} pending
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold">{formatCurrency(po.total_amount, symbol)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full uppercase ${STATUS_BADGE[po.status] || STATUS_BADGE.draft}`}>{po.status}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {po.status === 'draft' && MANAGE.includes(user?.role) && (
                            <Button size="sm" variant="outline" data-testid={`po-send-${po.id}`} onClick={() => cyclePO(po, 'sent')} title="Mark as sent">Send</Button>
                          )}
                          {RECEIVE.includes(user?.role) && ['sent', 'partial'].includes(po.status) && (
                            <Button size="sm" variant="outline" data-testid={`po-receive-${po.id}`} onClick={() => setOpenPo(po)} title="Log delivery"><Truck className="w-3.5 h-3.5" /></Button>
                          )}
                          {MANAGE.includes(user?.role) && bills.length > 0 && (
                            <Button size="sm" variant="outline" data-testid={`po-match-${po.id}`} onClick={() => setMatchOpen(po)} title="Match to bill"><Link2 className="w-3.5 h-3.5" /></Button>
                          )}
                          {MANAGE.includes(user?.role) && po.status === 'delivered' && (
                            <Button size="sm" variant="outline" data-testid={`po-close-${po.id}`} onClick={() => closePO(po)} title="Close PO"><CheckCircle2 className="w-3.5 h-3.5" /></Button>
                          )}
                          {['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO'].includes(user?.role) && (
                            <Button size="sm" variant="ghost" className="text-red-500" data-testid={`po-delete-${po.id}`} onClick={() => { if (window.confirm(`Delete PO ${po.po_number}?`)) deletePO(po); }}><Trash2 className="w-3.5 h-3.5" /></Button>
                          )}
                          <RowActions
                            testIdPrefix={`po-${po.id}`}
                            onDownloadPdf={() => downloadPoPdf(po)}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {pos.length === 0 && (
                  <tr><td colSpan="8" className="text-center text-muted-foreground py-12"><Package2 className="w-10 h-10 mx-auto text-slate-300 mb-2" />No purchase orders yet</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        )}

        {/* Receive dialog */}
        {openPo && (
          <ReceiveDialog
            po={openPo}
            onClose={() => setOpenPo(null)}
            onSaved={() => { setOpenPo(null); fetchAll(); }}
          />
        )}

        {/* Match-to-bill dialog */}
        {matchOpen && (
          <MatchDialog
            po={matchOpen}
            bills={bills}
            symbol={symbol}
            onClose={() => setMatchOpen(null)}
            onMatched={() => { setMatchOpen(null); fetchAll(); }}
          />
        )}
      </div>
    </Layout>
  );
}

// ===== Receive subdialog =====
function ReceiveDialog({ po, onClose, onSaved }) {
  const [recv, setRecv] = useState(() => Object.fromEntries((po.line_items || []).map(l => [l.id, 0])));
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const receipts = (po.line_items || [])
        .filter(l => Number(recv[l.id] || 0) > 0)
        .map(l => ({ line_item_id: l.id, qty: Number(recv[l.id] || 0) }));
      if (!receipts.length) { toast.error('Enter at least one received qty'); setSaving(false); return; }
      await axios.post(`${API}/purchase-orders/${po.id}/receive`, { receipts }, { withCredentials: true });
      toast.success('Receipt logged');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Receive failed');
    }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Receive Delivery — {po.po_number}</DialogTitle></DialogHeader>
        <div className="space-y-2 mt-2">
          {(() => {
            const totQty = (po.line_items || []).reduce((s, i) => s + Number(i.quantity || 0), 0);
            const recv = (po.line_items || []).reduce((s, i) => s + Number(i.qty_received || 0), 0);
            const pending = totQty - recv;
            if (pending > 0) {
              return (
                <div data-testid="grn-open-banner" className="p-2 rounded bg-rose-600 text-white text-xs font-bold flex items-center justify-between">
                  <span>GRN OPEN — {pending} unit(s) still pending</span>
                  <span className="text-rose-100 font-normal">Will remain open until fully delivered</span>
                </div>
              );
            }
            return null;
          })()}
          {(po.line_items || []).map(l => {
            const remaining = Number(l.quantity) - Number(l.qty_received || 0);
            const isPending = remaining > 0;
            return (
              <div key={l.id} className={`grid grid-cols-12 gap-2 items-center text-sm p-2 rounded ${isPending ? 'bg-rose-50 border-l-4 border-rose-500' : 'bg-emerald-50 border-l-4 border-emerald-500'}`}>
                <div className="col-span-7">
                  <div className={`font-medium ${isPending ? 'text-rose-800' : 'text-emerald-800'}`}>
                    {l.item_name}
                    {isPending && <span className="ml-2 text-[10px] font-bold uppercase tracking-wide">PENDING</span>}
                    {!isPending && <span className="ml-2 text-[10px] font-bold uppercase tracking-wide">✓ FULLY RECEIVED</span>}
                  </div>
                  <div className="text-xs text-muted-foreground">Ordered {l.quantity} {l.unit} · Already received {l.qty_received || 0} · <span className={isPending ? 'text-rose-700 font-bold' : ''}>Remaining {remaining}</span></div>
                </div>
                <div className="col-span-5">
                  <Input
                    type="number"
                    data-testid={`receive-qty-${l.id}`}
                    value={recv[l.id]}
                    max={remaining}
                    min={0}
                    disabled={!isPending}
                    onChange={(e) => setRecv(r => ({ ...r, [l.id]: e.target.value }))}
                    placeholder={isPending ? `max ${remaining}` : 'complete'}
                  />
                </div>
              </div>
            );
          })}
          <Button data-testid="submit-receive-btn" disabled={saving} onClick={submit} className="w-full mt-3 bg-amber-500 hover:bg-amber-600 text-slate-900">{saving ? 'Saving…' : 'Log Receipt'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===== AP-match subdialog =====
function MatchDialog({ po, bills, symbol, onClose, onMatched }) {
  const candidates = bills.filter(b => (b.supplier_id || '') === (po.supplier_id || '') && !b.po_id);
  const [billId, setBillId] = useState(candidates[0]?.id || '');
  const [result, setResult] = useState(null);
  const submit = async () => {
    if (!billId) return;
    try {
      const r = await axios.post(`${API}/purchase-orders/${po.id}/match-bill/${billId}`, {}, { withCredentials: true });
      setResult(r.data);
      toast.success('Bill linked to PO');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Match failed');
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Match Bill to PO {po.po_number}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label>Choose unmatched bill from same supplier</Label>
            <Select value={billId} onValueChange={setBillId}>
              <SelectTrigger data-testid="match-bill-select"><SelectValue placeholder="Pick bill" /></SelectTrigger>
              <SelectContent>
                {candidates.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.bill_number} — {formatCurrency(b.total_amount || b.amount, symbol)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {candidates.length === 0 && <p className="text-xs text-rose-600 mt-1">No unmatched bills found for this supplier</p>}
          </div>
          {result && (
            <div className={`p-3 rounded-lg text-sm ${result.status === 'match' ? 'bg-emerald-50 text-emerald-900' : 'bg-amber-50 text-amber-900'}`}>
              <div>PO total: <strong>{formatCurrency(result.po_total, symbol)}</strong></div>
              <div>Bill total: <strong>{formatCurrency(result.bill_total, symbol)}</strong></div>
              <div>Variance: <strong>{formatCurrency(result.variance, symbol)}</strong> ({result.status})</div>
            </div>
          )}
          <Button data-testid="submit-match-btn" disabled={!billId} onClick={submit} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900">Match & Compare</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
